import { forwardRef } from 'react'
import { Link } from '@inertiajs/react'
import { Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:pointer-events-none select-none active:scale-[0.98]'

const variants: Record<Variant, string> = {
  primary: 'bg-ink-900 text-white hover:bg-ink-800 shadow-sm',
  secondary: 'bg-accent-500 text-white hover:bg-accent-600 shadow-sm',
  outline: 'border border-ink-200 text-ink-900 hover:bg-ink-50',
  ghost: 'text-ink-700 hover:bg-ink-100',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-11 px-5 text-sm',
  lg: 'h-13 px-7 text-base',
}

type ButtonProps = {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  href?: string
  method?: 'get' | 'post' | 'delete'
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'>

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    className,
    children,
    href,
    method,
    onClick,
    disabled,
    ...rest
  } = props
  const classes = cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)

  if (href) {
    return (
      <Link
        href={href}
        method={method}
        className={classes}
        onClick={onClick as unknown as (e: React.MouseEvent) => void}
        aria-label={props['aria-label']}
      >
        {children}
      </Link>
    )
  }

  return (
    <button
      ref={ref}
      className={classes}
      disabled={loading || disabled}
      onClick={onClick}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  )
})
Button.displayName = 'Button'
